export function preloadImages(scene) {
  const base = 'assets/GameAssets';

  // Template platform assets
  scene.load.image('hintBackground', `${base}/hintBackground.png`);
  scene.load.image('hintCloseButton', `${base}/hintCloseButton.png`);
  scene.load.image('hint_1', `${base}/hint_1.png`);
  scene.load.image('hint_2', `${base}/hint_2.png`);
  scene.load.image('hover_nextButton', `${base}/hover_nextButton.png`);
  scene.load.image('hover_playButton', `${base}/hover_playButton.png`);
  scene.load.image('hover_replayButton', `${base}/hover_replayButton.png`);
  scene.load.image('nextButton', `${base}/nextButton.png`);
  scene.load.image('overlay', `${base}/overlay.png`);
  scene.load.image('playButton', `${base}/playButton.png`);
  scene.load.image('replayButton', `${base}/replayButton.png`);
  scene.load.image('replayOverlay', `${base}/replayOverlay.png`);
  scene.load.image('s_hover_replayButton', `${base}/s_hover_replayButton.png`);
  scene.load.image('s_replayButton', `${base}/s_replayButton.png`);

  // Balance Out — shop / start scene
  scene.load.image('game-name', `${base}/game-name.png`);
  scene.load.image('new-table', `${base}/new-table.png`);
  scene.load.image('left-shelf', `${base}/left-shelf.png`);
  scene.load.image('left-shelf-items', `${base}/left-shelf-items.png`);
  scene.load.image('right-shelf', `${base}/right-shelf.png`);
  scene.load.image('right-shelf-items', `${base}/right-shelf-items.png`);
  scene.load.image('new-right-shelf', `${base}/new-right-shelf.png`);
  scene.load.image('right-shelf-tutorial', `${base}/right-shelf-tutorial.png`);
  scene.load.image('item-blue-left', `${base}/item-blue-left.png`);
  scene.load.image('item-blue-right', `${base}/item-blue-right.png`);
  scene.load.image('item-green', `${base}/item-green.png`);
  scene.load.image('item-pink', `${base}/item-pink.png`);
  scene.load.image('item-yellow-left', `${base}/item-yellow-left.png`);
  scene.load.image('item-yellow-right', `${base}/item-yellow-right.png`);

  // Balance Out — game scene
  scene.load.image('scale', `${base}/scale.png`);
  scene.load.image('balance-stand', `${base}/balance-stand.png`);
  scene.load.image('balance-rod', `${base}/balance-rod.png`);
  scene.load.image('balance-tray', `${base}/balance-tray.png`);
  scene.load.image('balance-tray-top', `${base}/balance-tray-top.png`);
  scene.load.image('arrow', `${base}/arrow.png`);
  scene.load.image('handCursor', `${base}/handCursor.png`);
  scene.load.image('letsWeigh', `${base}/letsWeigh.png`);
  scene.load.image('greenLight', `${base}/greenLight.png`);
  for (let kg = 1; kg <= 10; kg++) {
    scene.load.image(`weight-block-${kg}kg`, `${base}/weight-block-${kg}kg.png`);
  }
  for (let kg = 1; kg <= 10; kg++) {
    scene.load.image(`weight-bag-${kg}kg`, `${base}/weight-bag-${kg}kg.png`);
  }
  // Green-bordered (correct) + red-bordered (wrong) sack variants —
  // swapped in via setTexture instead of overlaying a separate glow.
  // Only kg values that have an imported variant are loaded; missing
  // ones fall through to the unhighlighted base texture.
  [1, 2, 3, 4, 6].forEach(kg => {
    scene.load.image(`weight-bag-${kg}kg-right`, `${base}/weight-bag-${kg}kg-right.png`);
    scene.load.image(`weight-bag-${kg}kg-wrong`, `${base}/weight-bag-${kg}kg-wrong.png`);
  });

  // Pre-baked per-kg bag variants for the non-sack styles. Each entry
  // gives the kg values that have a `<base>-<kg>kg.png` import. Where
  // a `-right` / `-wrong` highlight variant also exists it gets loaded
  // alongside; missing variants fall through to the runtime-tinted
  // silhouette already wired up in GameScene.spawnAnswerHighlight.
  const PREBAKED_BAGS = [
    {
      base: 'bag-card-purple',
      kgs: [1, 2, 3, 4, 5, 6],
      right: [1, 2, 3, 4, 5, 6],
      wrong: [1, 2, 3, 4, 5, 6],
    },
    {
      base: 'bag-jar-yellow',
      kgs: [2, 3, 4, 7],
      right: [2, 3, 4, 7],
      wrong: [2, 3, 4, 7],
    },
    {
      base: 'bag-carton-green',
      kgs: [1, 3, 4, 6],
      right: [1, 4, 6],
      wrong: [1, 3, 4],
    },
    {
      base: 'bag-box-yellow',
      kgs: [2, 3, 4, 5],
      right: [2, 3, 4, 5],
      wrong: [2, 3, 4, 5],
    },
    {
      base: 'bag-ticket-purple',
      kgs: [2, 3, 4, 5],
      right: [2, 3, 4, 5],
      wrong: [2, 3, 4, 5],
    },
  ];
  PREBAKED_BAGS.forEach(({ base: b, kgs, right, wrong }) => {
    kgs.forEach(kg => scene.load.image(`${b}-${kg}kg`, `${base}/${b}-${kg}kg.png`));
    right.forEach(kg =>
      scene.load.image(`${b}-${kg}kg-right`, `${base}/${b}-${kg}kg-right.png`),
    );
    wrong.forEach(kg =>
      scene.load.image(`${b}-${kg}kg-wrong`, `${base}/${b}-${kg}kg-wrong.png`),
    );
  });

  // Per-question right/wrong highlight glows that sit behind the bag
  // stack on the right pan. The shape of each pair mirrors the bag
  // style used by its question (sack/card/jar/carton/box/ticket).
  const highlightStyles = ['sack', 'card', 'jar', 'carton', 'box', 'ticket'];
  highlightStyles.forEach(style => {
    scene.load.image(`hl-right-${style}`, `${base}/hl-right-${style}.png`);
    scene.load.image(`hl-wrong-${style}`, `${base}/hl-wrong-${style}.png`);
  });

  // Per-question bag visuals. The "sack" style is the kg-labelled blue
  // bag already loaded above (weight-bag-Xkg). The remaining styles
  // are neutral-coloured PNGs without kg labels — GameScene paints the
  // kg number on top at runtime.
  scene.load.image('bag-card-purple', `${base}/bag-card-purple.png`);
  scene.load.image('bag-jar-yellow', `${base}/bag-jar-yellow.png`);
  scene.load.image('bag-carton-green', `${base}/bag-carton-green.png`);
  scene.load.image('bag-box-yellow', `${base}/bag-box-yellow.png`);
  scene.load.image('bag-ticket-purple', `${base}/bag-ticket-purple.png`);
}
